// src/components/chat/SegmentRenderer.tsx
// Renders an ordered list of message segments (text + tool pills).
// Used by both MessageBubble (persisted) and MessageList (streaming).

import React from 'react';
import { Loader2, CheckCircle2, XCircle } from 'lucide-react';
import { clsx } from 'clsx';
import MarkdownMessage from './MarkdownMessage';
import { getToolDisplay } from './toolDisplay';
import RoutineProposalCard from './RoutineProposalCard';
import RoutineUpdateProposalCard from './RoutineUpdateProposalCard';
import RoutineDeletionProposalCard from './RoutineDeletionProposalCard';
import SegmentProposalCard from './SegmentProposalCard';
import SegmentUpdateProposalCard from './SegmentUpdateProposalCard';
import type { MessageSegment } from '@/types/chat';

interface SegmentRendererProps {
  segments: MessageSegment[];
  isStreaming?: boolean;
  /** Required for any tool segments that render proposal cards — passed
   *  through as `birthed_from_chat_session_id` on Approve. */
  chatThreadId?: string;
  /** When provided, completed/failed tool pills become clickable buttons
   *  that invoke this handler with the toolCallId. Running pills remain
   *  static (their llm_tool_calls row may not exist yet). */
  onToolCallClick?: (toolCallId: string) => void;
}

const ToolPill: React.FC<{
  segment: MessageSegment & { type: 'tool' };
  onClick?: () => void;
}> = ({ segment, onClick }) => {
  const { icon: Icon, label } = getToolDisplay(segment.toolName);
  const isRunning = segment.status === 'running';

  const colorClasses = isRunning
    ? 'bg-accent text-primary'
    : segment.status === 'completed'
      ? 'bg-success/15 text-success'
      : 'bg-destructive/15 text-destructive';

  const content = (
    <>
      {isRunning ? (
        <Loader2 className="w-3 h-3 animate-spin" />
      ) : segment.status === 'completed' ? (
        <CheckCircle2 className="w-3 h-3" />
      ) : (
        <XCircle className="w-3 h-3" />
      )}
      <Icon className="w-3 h-3" />
      <span>{label}</span>
    </>
  );

  const baseClasses =
    'inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium transition-all';

  if (onClick) {
    return (
      <button
        type="button"
        onClick={onClick}
        className={clsx(
          baseClasses,
          colorClasses,
          'cursor-pointer hover:brightness-95 dark:hover:brightness-110',
          'focus:outline-none focus:ring-2 focus:ring-ring/40'
        )}
      >
        {content}
      </button>
    );
  }

  return <div className={clsx(baseClasses, colorClasses)}>{content}</div>;
};

const SegmentRenderer: React.FC<SegmentRendererProps> = ({ segments, isStreaming = false, chatThreadId, onToolCallClick }) => {
  if (segments.length === 0 && isStreaming) {
    return (
      <div className="flex items-center gap-2 text-sm text-muted-foreground">
        <Loader2 className="w-4 h-4 animate-spin text-primary" />
        <span>Thinking...</span>
      </div>
    );
  }

  if (segments.length === 0) return null;

  const lastTextIndex = findLastTextIndex(segments);

  return (
    <div className="flex flex-col gap-1.5">
      {segments.map((seg, idx) => {
        if (seg.type === 'text') {
          if (!seg.text && !isStreaming) return null;
          const isActiveTextSegment = isStreaming && idx === lastTextIndex;
          return (
            <div key={idx}>
              {seg.text ? (
                <MarkdownMessage content={seg.text} />
              ) : null}
              {isActiveTextSegment && (
                <span className="inline-block w-1.5 h-4 ml-0.5 bg-primary animate-pulse rounded-sm" />
              )}
            </div>
          );
        }

        if (seg.type === 'tool') {
          // Render rich inline cards for tool results that ship a structured
          // payload (e.g. routine proposals). Falls back to the standard
          // status pill for everything else.
          if (seg.payload?.type === 'routine_proposal' && chatThreadId) {
            return (
              <div key={seg.toolCallId} className="my-1.5">
                <RoutineProposalCard
                  proposal={seg.payload}
                  chatThreadId={chatThreadId}
                />
              </div>
            );
          }
          if (seg.payload?.type === 'routine_update_proposal') {
            return (
              <div key={seg.toolCallId} className="my-1.5">
                <RoutineUpdateProposalCard proposal={seg.payload} />
              </div>
            );
          }
          if (seg.payload?.type === 'routine_deletion_proposal') {
            return (
              <div key={seg.toolCallId} className="my-1.5">
                <RoutineDeletionProposalCard proposal={seg.payload} />
              </div>
            );
          }
          if (seg.payload?.type === 'segment_proposal') {
            return (
              <div key={seg.toolCallId} className="my-1.5">
                <SegmentProposalCard proposal={seg.payload} />
              </div>
            );
          }
          if (seg.payload?.type === 'segment_update_proposal') {
            return (
              <div key={seg.toolCallId} className="my-1.5">
                <SegmentUpdateProposalCard proposal={seg.payload} />
              </div>
            );
          }

          const handlePillClick =
            onToolCallClick && seg.status !== 'running' && seg.toolCallId
              ? () => onToolCallClick(seg.toolCallId)
              : undefined;
          return (
            <div key={seg.toolCallId} className="flex flex-wrap gap-1.5 my-0.5">
              <ToolPill segment={seg} onClick={handlePillClick} />
            </div>
          );
        }

        return null;
      })}
    </div>
  );
};

function findLastTextIndex(segments: MessageSegment[]): number {
  for (let i = segments.length - 1; i >= 0; i--) {
    if (segments[i].type === 'text') return i;
  }
  return -1;
}

export default SegmentRenderer;
